import { DatePipe, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { combineLatest, finalize, map, Observable } from 'rxjs';

import { BcoService, EnvironmentService, ErableHttpService, IdUtils, UaService } from '@ecm/consumer';
import { EcmDispatcherModule, EnvUtils, StorageService, StoreService } from '@ecm/dispatcher';
import {
    Brand,
    ContextService,
    Contract,
    ContractsBusinessModel,
    PortfolioHelper,
    PortfolioService,
} from '@ecm/portfolio';

import { BrowsingData } from '../business/kenobi/browsing-data';
import { KenobiParams } from '../business/kenobi/kenobi-params';
import { KenobiTrackingData } from '../business/kenobi/kenobi-tracking-data';
import { PostData } from '../business/kenobi/post-data.interface';
import { Status } from '../business/kenobi/status.enum';
import { Type } from '../business/kenobi/type.enum';
import { CodeIdorResolver } from './code-idor-resolver.service';

@Injectable({
    providedIn: 'root',
})
export class KenobiService {
    /**
     * Class name for console logs
     * @type {string}
     */
    private readonly className: string = 'KenobiService';

    /**
     * Objects access keys
     */
    private readonly ID_KEY: string = 'id';
    private readonly CID_KEY: string = 'cid';

    public constructor(
        @Inject(PLATFORM_ID) private platformId: any,
        private environmentService: EnvironmentService,
        private http: ErableHttpService,
        private bcoService: BcoService,
        private uaService: UaService,
        private portfolioService: PortfolioService,
        private contextService: ContextService,
        private datePipe: DatePipe,
        private readonly codeIdorResolver: CodeIdorResolver,
    ) {}

    /**
     * Get legal status (postureJuridique Kenobi property) from advisor role
     * @param {string} role
     * @return {string}
     */
    private static getLegalStatusFromRole(role: string): string {
        const statusFromRole = {
            SU: 'F',
            AST: 'I',
            CONV: 'I',
            DISTRIB: 'I',
        };
        let status = '';
        role = role.toUpperCase();
        if (role.length === 1) {
            status = role;
        } else if (statusFromRole[role] !== undefined) {
            status = statusFromRole[role];
        }
        return status;
    }

    /**
     * Tracks view in Kenobi
     * @param {KenobiTrackingData} kenobiTrackingData View's Kenobi tracking data
     * @deprecated use `trackMulti` instead
     */
    public track(kenobiTrackingData: KenobiTrackingData): void {
        // Tracking only on Browser platform
        if (isPlatformBrowser(this.platformId)) {
            this.contextService.isSosh().subscribe((isSosh: boolean) => {
                // Manage CID
                const currentCid: string = StoreService.has('currentContract')
                    ? StoreService.get('currentContract')[this.CID_KEY]
                    : 'unavailable';
                const cid: string =
                    kenobiTrackingData.cid === null || kenobiTrackingData.cid === undefined
                        ? currentCid
                        : kenobiTrackingData.cid;

                const brand: Brand = this.getBrand(isSosh);
                const browsingData: BrowsingData[] = this.getBrowsingData(kenobiTrackingData, brand);

                // forge final body to post
                let requestBody: PostData;
                if (kenobiTrackingData.type === Type.CONTRACT) {
                    requestBody = {
                        browsingData: browsingData,
                        globalData: [
                            {
                                key: 'cid',
                                value: cid,
                            },
                        ],
                    };
                } else {
                    requestBody = {
                        browsingData: browsingData,
                    };
                }

                // set pageId
                const pageId: string = kenobiTrackingData.pageId ? kenobiTrackingData.pageId : 'undefined';

                // perform request to Kenobi
                this.postRequest(requestBody, pageId, false, true);
            });
        }
    }

    /**
     * Tracks records in Kenobi with generique event
     * @param {KenobiParams | KenobiParams[]} kenobiParams Kenobi params (can be an Array, or not)
     * @param {string} pageId Page ID for BCO log of statProcess POST request
     * @param {boolean} isVisitor Set to `true` on visitor pages, `false` on other pages
     * @param {string} [cid] Value for for `globalData.cid` property (optional)
     */
    public trackMulti(
        kenobiParams: KenobiParams | KenobiParams[],
        pageId: string,
        isVisitor = false,
        cid?: string,
    ): void {
        this.trackInternal(kenobiParams, pageId, isVisitor, cid, 'generique');
    }

    /**
     * Tracks records in Kenobi with generiqueV2 event
     * @param {KenobiParams | KenobiParams[]} kenobiParams Kenobi params (can be an Array, or not)
     * @param {string} pageId Page ID for BCO log of statProcess POST request
     * @param {boolean} isVisitor Set to `true` on visitor pages, `false` on other pages
     * @param {string} [cid] Value for for `globalData.cid` property (optional)
     */
    public trackMultiWithEventGeneriqueV2(
        kenobiParams: KenobiParams | KenobiParams[],
        pageId: string,
        isVisitor: boolean,
        cid?: string,
    ): void {
        this.trackInternal(kenobiParams, pageId, isVisitor, cid, 'generiqueV2');
    }

    /**
     * Tracks records in Kenobi
     * @param {KenobiParams | KenobiParams[]} kenobiParams Kenobi params (can be an Array, or not)
     * @param {string} pageId Page ID for BCO log of statProcess POST request
     * @param {boolean} isVisitor Set to `true` on visitor pages, `false` on other pages
     * @param {string} [cid] Value for for `globalData.cid` property (optional)
     * @param {string} event indicate if the event is generique or generiqueV2
     */
    public trackInternal(
        kenobiParams: KenobiParams | KenobiParams[],
        pageId: string,
        isVisitor: boolean,
        cid?: string,
        event?: string,
    ): void {
        // Tracking only on Browser platform
        if (isPlatformBrowser(this.platformId)) {
            const kenobiParamsArray: KenobiParams[] = kenobiParams instanceof Array ? kenobiParams : [kenobiParams];

            this.contextService.isSosh().subscribe((isSosh: boolean) => {
                const brand: Brand =
                    EnvUtils.getBasicatCode() === 'SDI' ? this.getAssistanceBrand() : this.getBrand(isSosh);

                // forge final body to post
                const requestBody: PostData = {
                    browsingData: this.getBrowsingDataMulti(kenobiParamsArray, brand, event),
                };

                if (isVisitor) {
                    requestBody.globalData = [
                        {
                            key: 'cid',
                            value: 'unavailable',
                        },
                    ];
                } else if (cid) {
                    requestBody.globalData = [
                        {
                            key: 'cid',
                            value: cid,
                        },
                    ];
                }

                // perform request to Kenobi
                this.postRequest(requestBody, pageId, isVisitor, true);
            });
        }
    }

    /**
     * Tracks portfolio in Kenobi (with `CONTPF` functionality)
     * Performs a POST request for each contract
     * Will publish true in observable when logs have been sent, do not return request status
     * warning : do not use this in Features (used only in `EcmComponent`)
     * @return {Observable<boolean>}
     */
    public trackPortfolio(): Observable<boolean> {
        return combineLatest([this.contextService.isSosh(), this.portfolioService.getRawContracts()]).pipe(
            map(([isSosh, portfolio]: [boolean, ContractsBusinessModel]) => {
                const brand: Brand = this.getBrand(isSosh);
                const loggedContract = PortfolioHelper.getLoggedContract(portfolio.contracts);

                // track 'INFOLOG' (one tracking for whole portfolio, even if it is empty)
                const loggedContractCid = loggedContract?.cid || 'unavailable';
                const kenobiINFOLOG: KenobiParams = new KenobiParams('INFOLOG', '*', Status.OK);
                kenobiINFOLOG.comment = '{{oidval}}';
                const infoLogRequestBody: PostData = {
                    browsingData: this.getBrowsingDataMulti([kenobiINFOLOG], brand),
                    globalData: [
                        {
                            key: 'cid',
                            value: loggedContractCid,
                        },
                    ],
                };
                // perform request to Kenobi (without flushing BCO logs right now)
                this.postRequest(infoLogRequestBody, IdUtils.generateRandomId(), false, false);

                // track 'CONTPF' (one tracking for each contract in portfolio)
                const isAnyVimEligibleFound = portfolio.contracts.some(
                    (contract) => contract.telco?.state.toUpperCase() === 'VIM_ELIGIBLE',
                );
                portfolio.contracts.forEach((contract: Contract) => {
                    const kenobiCONTPF: KenobiParams = new KenobiParams('CONTPF', '*', Status.OK);
                    // set `commentaire` property with "INSTALLATION" value, only for RAIPONCE contracts
                    if (contract.status.toUpperCase() === 'EN CREATION') {
                        kenobiCONTPF.comment = 'INSTALLATION';
                    }
                    // set `commentaire` property with "RESTREINT" value on logged contract if there is any VIM_ELIGIBLE contract
                    else if (contract.telco?.state.toUpperCase() === 'LOGGED' && isAnyVimEligibleFound) {
                        kenobiCONTPF.comment = 'RESTREINT';
                    }
                    // set `commentaire` property with "PUSH" value, only for SUGGESTED contracts
                    else if (contract.telco?.state.toUpperCase() === 'SUGGESTED') {
                        kenobiCONTPF.comment = 'PUSH';
                    }

                    // forge final body to post
                    const requestBody: PostData = {
                        browsingData: this.getBrowsingDataMulti([kenobiCONTPF], brand),
                        globalData: [
                            {
                                key: 'cid',
                                value: contract.cid,
                            },
                        ],
                    };
                    // perform request to Kenobi (without flushing BCO logs right now)
                    this.postRequest(requestBody, IdUtils.generateRandomId(), false, false);
                });

                // when all requests are done, flush BCO logs
                this.bcoService.flushLogs();

                return true;
            }),
        );
    }

    /**
     * Tracks view in Kenobi
     * @param {KenobiTrackingData} kenobiTrackingData View's Kenobi tracking data
     * @deprecated use `trackMulti` method instead
     */
    public trackView(kenobiTrackingData: KenobiTrackingData): void {
        // eslint-disable-next-line deprecation/deprecation -- usage should be removed with the removing of this method
        this.track(kenobiTrackingData);
    }

    /**
     * Tracks view in Kenobi (visitor mode)
     * @param {KenobiTrackingData} kenobiTrackingData View's Kenobi tracking data
     * @deprecated use `trackMulti` instead (with param `isVisitor` to `true`)
     */
    public trackVisitor(kenobiTrackingData: KenobiTrackingData): void {
        // Tracking only on Browser platform
        if (isPlatformBrowser(this.platformId)) {
            this.contextService.isSosh().subscribe((isSosh: boolean) => {
                const brand: Brand = this.getBrand(isSosh);
                const browsingData: BrowsingData[] = this.getBrowsingData(kenobiTrackingData, brand);

                // forge final body to post
                const requestBody: PostData = {
                    browsingData: browsingData,
                    globalData: [
                        {
                            key: 'cid',
                            value: 'unavailable',
                        },
                    ],
                };

                // set pageId
                const pageId: string = kenobiTrackingData.pageId ? kenobiTrackingData.pageId : 'undefined';

                // perform request to Kenobi
                this.postRequest(requestBody, pageId, true, true);
            });
        }
    }

    /**
     * Get`browsingData` Kenobi property with provided tracking data
     * @param {KenobiTrackingData} kenobiTrackingData
     * @param {Brand} brand Brand (used for `applicationEmettrice` Kenobi param)
     * @returns {BrowsingData[]}
     */
    private getBrowsingData(kenobiTrackingData: KenobiTrackingData, brand: Brand): BrowsingData[] {
        const browsingData: BrowsingData[] = [];
        // Get current date and apply Kenobi format
        const date: Date = new Date();
        const dateString: string = this.datePipe.transform(date, 'yyyy-MM-ddTHH:mm:ssZZZZZ');
        const event = 'generique';

        // initialize and set functionalities array
        let functionalities: string[] = [];
        if (kenobiTrackingData.functionalities) {
            functionalities = kenobiTrackingData.functionalities;
        } else {
            // eslint-disable-next-line deprecation/deprecation -- else will be removed when functionality is not used
            functionalities.push(kenobiTrackingData.functionality);
        }

        // for each functionality, create a browsingData item
        functionalities.forEach((functionality: string) => {
            const browsingDataItem: BrowsingData = new BrowsingData();
            browsingDataItem.timestamp = dateString;
            browsingDataItem.event = event;
            browsingDataItem.params = this.getBrowsingDataParams(kenobiTrackingData, functionality, brand);
            browsingData.push(browsingDataItem);
        });

        return browsingData;
    }

    /**
     * @param {KenobiParams[]} kenobiParams KenobiParams Array
     * @param {Brand} brand Brand (used for `applicationEmettrice` Kenobi param)
     * @param {string} event indicate if the event is generique or generiqueV2
     * @returns {BrowsingData[]}
     */
    private getBrowsingDataMulti(kenobiParams: KenobiParams[], brand: Brand, event = 'generique'): BrowsingData[] {
        const browsingData: BrowsingData[] = [];
        // Get current date and apply Kenobi format
        const date: Date = new Date();
        const dateString: string = this.datePipe.transform(date, 'yyyy-MM-ddTHH:mm:ssZZZZZ');

        // for each functionality, create a browsingData item
        kenobiParams.forEach((item: KenobiParams) => {
            const browsingDataItem: BrowsingData = new BrowsingData();
            browsingDataItem.timestamp = dateString;
            browsingDataItem.event = event;
            browsingDataItem.params = this.getBrowsingDataParamsMulti(item, brand);
            browsingData.push(browsingDataItem);
        });

        return browsingData;
    }

    /**
     * Get browsingData.params property for a functionality
     * @param {KenobiTrackingData} kenobiTrackingData
     * @param {string} functionality
     * @param {Brand} brand Brand (used for `applicationEmettrice` Kenobi param)
     * @returns {any[]}
     */
    private getBrowsingDataParams(kenobiTrackingData: KenobiTrackingData, functionality: string, brand: Brand): any[] {
        // initialize object with mandatory params
        let params: any[] = [
            {
                name: 'rubrique',
                value: kenobiTrackingData.section,
            },
            {
                name: 'fonctionnalite',
                value: functionality,
            },
            {
                name: 'codeRetour',
                value: kenobiTrackingData.status,
            },
        ];

        params = params.concat(this.getSessionParams(brand));
        params = params.concat(this.getOptionalParams(kenobiTrackingData));
        params = params.concat(this.getAdvisorParams());

        return params;
    }

    /**
     * Get browsingData.params property
     * @param {KenobiParams} kenobiParams KenobiParams Array
     * @param {Brand} brand Brand (used for `applicationEmettrice` Kenobi param)
     * @returns {any[]}
     */
    private getBrowsingDataParamsMulti(kenobiParams: KenobiParams, brand: Brand): any[] {
        // initialize object with mandatory params
        let params: any[] = [
            {
                name: 'rubrique',
                value: kenobiParams.section,
            },
            {
                name: 'fonctionnalite',
                value: kenobiParams.functionality,
            },
            {
                name: 'codeRetour',
                value: kenobiParams.status,
            },
        ];

        params = params.concat(this.getSessionParams(brand));
        params = params.concat(this.getOptionalParams(kenobiParams));
        params = params.concat(this.getAdvisorParams());

        return params;
    }

    /**
     * Get session params
     * @param {Brand} brand Brand (used for `applicationEmettrice` Kenobi param)
     * @returns {any[]}
     */
    private getSessionParams(brand: Brand): any[] {
        const sessionId: string = StoreService.get('session')[this.ID_KEY];
        // a NDE native app hosting the webview is always considered as mobile
        const isMobile: boolean = !!this.codeIdorResolver.ndeAppBrand || this.uaService.isMobile();
        const brandSuffix: string = brand === Brand.SOSH ? '_S' : '_O';
        let canalCode: string;
        let app: string;

        if (EnvUtils.getBasicatCode() === 'SDI') {
            // Assistance (SOLI) values
            app = isMobile ? 'SOLI_MOBILE' : 'SOLI_WEB';
            canalCode = isMobile ? '15' : '14';
        } else {
            // ECM values
            app = isMobile ? 'ECM_MOBILE' : 'ECM_WEB';
            canalCode = isMobile ? '8' : '1';
        }
        app = app + brandSuffix;

        return [
            {
                name: 'numeroSession',
                value: sessionId,
            },
            {
                name: 'codeCanal',
                value: canalCode,
            },
            {
                name: 'parc',
                value: '*****',
            },
            {
                name: 'duree',
                value: '0',
            },
            {
                name: 'codeIdor',
                value: this.codeIdorResolver.codeIdor,
            },
            {
                name: 'applicationEmettrice',
                value: app,
            },
        ];
    }

    /**
     * Get optional params
     * @param {KenobiTrackingData} kenobiTrackingData
     * @returns {any[]}
     */
    private getOptionalParams(kenobiTrackingData: KenobiTrackingData | KenobiParams): any[] {
        const optionalParams: any[] = [];
        if (kenobiTrackingData.comment) {
            optionalParams.push({ name: 'commentaire', value: kenobiTrackingData.comment });
        }
        if (kenobiTrackingData.offer) {
            optionalParams.push({ name: 'offre', value: kenobiTrackingData.offer });
        }
        if (kenobiTrackingData.price) {
            optionalParams.push({ name: 'valeurMonetaire', value: kenobiTrackingData.price });
        }
        if (kenobiTrackingData.line) {
            optionalParams.push({ name: 'ligne', value: kenobiTrackingData.line });
        }
        if (kenobiTrackingData.lineType) {
            optionalParams.push({ name: 'typeLigne', value: kenobiTrackingData.lineType });
        }
        return optionalParams;
    }

    /**
     * Get Advisor params
     * (set by ecm-consumer in StoreService, based on JWT in HttpResponse)
     * @returns {any[]}
     */
    private getAdvisorParams(): any[] {
        const advisorParams: any[] = [];
        const advisor = StorageService.get('advisor');

        if (advisor?.alliance) {
            advisorParams.push({ name: 'conseiller', value: advisor.alliance });
        }
        if (advisor?.activityteam) {
            advisorParams.push({ name: 'entiteHierarchique', value: advisor.activityteam });
        }
        if (advisor?.team) {
            advisorParams.push({ name: 'entiteActivite', value: advisor.team });
        }
        if (advisor?.cuid) {
            advisorParams.push({ name: 'cuidClient', value: advisor.cuid });
        }
        if (advisor?.role) {
            advisorParams.push({
                name: 'postureJuridique',
                value: KenobiService.getLegalStatusFromRole(advisor.role),
            });
        }
        if (advisor?.channel && advisor?.basicat?.toUpperCase() === 'H2H') {
            advisorParams.push({ name: 'partenaire', value: advisor.channel });
        } else if (advisor?.basicat) {
            advisorParams.push({ name: 'partenaire', value: advisor.basicat });
        }

        return advisorParams;
    }

    /**
     * Performs POST to Kenobi API (statProcess)
     * and flush BCO logs
     * @param {PostData} body Kenobi data
     * @param {string} pageId Page ID for BCO log of statProcess request
     * @param {boolean} isVisitor Set to `true` on visitor pages, `false` on other pages
     * @param {boolean} flushLogs Set to `true` to flush BCO logs on success callback
     */
    private postRequest(body: PostData, pageId: string, isVisitor: boolean, flushLogs: boolean): void {
        const apiKey: string = isVisitor ? 'STAT_PROCESS_VISITOR_URL' : 'STAT_PROCESS_URL';

        const url = this.environmentService.instant(`API.TRACKER.${apiKey}`);

        this.http
            .post(url, body, {
                partner: 'ZBI',
                service: 'statProcess',
                functionality: 'KENOBI',
                feature: 'monitoring',
                pageId,
            })
            .pipe(finalize(() => flushLogs && this.bcoService.flushLogs()))
            .subscribe({
                next: () => EcmDispatcherModule.sendConsoleInfo(this.className, 'Successful POST to ' + url),
                error: () => EcmDispatcherModule.sendConsoleError(this.className, 'Error performing POST to ' + url),
            });
    }

    /**
     * Get Brand (used for `applicationEmettrice` Kenobi param)
     * Brand of the NDE native app hosting the webview first, user brand otherwise
     * @param {boolean} isSosh
     * @returns {Brand}
     */
    private getBrand(isSosh: boolean): Brand {
        return this.codeIdorResolver.ndeAppBrand ?? (isSosh ? Brand.SOSH : Brand.ORANGE);
    }

    /**
     * Get Assistance (SOLI) Brand (used for `applicationEmettrice` Kenobi param), based on environment
     * @returns {Brand}
     */
    private getAssistanceBrand(): Brand {
        return this.environmentService.instant()?.BRAND?.toUpperCase() === Brand.SOSH ? Brand.SOSH : Brand.ORANGE;
    }
}
